import React, { useCallback, useEffect, useState } from 'react';
import { ShieldAlert, Lock, KeyRound, UserCog, Search, RefreshCw, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { apiCall } from '../../utils/api';
import { toast } from '../ui/sonner';

const SEV = {
  high: 'bg-red-50 border-red-200 text-red-700',
  medium: 'bg-amber-50 border-amber-200 text-amber-700',
  info: 'bg-emerald-50 border-emerald-200 text-emerald-700',
};

export default function SecurityMonitorPanel() {
  const [data, setData] = useState(null);
  const [events, setEvents] = useState([]);
  const [days, setDays] = useState(7);
  const [q, setQ] = useState('');

  const load = useCallback(async (d = days, query = q) => {
    try {
      const [o, e] = await Promise.all([
        apiCall(`/api/admin/security/overview?days=${d}`),
        apiCall(`/api/admin/security/events?limit=100${query ? `&q=${encodeURIComponent(query)}` : ''}`),
      ]);
      setData(o); setEvents(e.events || []);
    } catch (err) { toast.error(err.message); setData({}); }
  }, [days, q]);
  useEffect(() => { load(); }, []);  

  if (!data) return <p className="text-sm text-slate-500 py-8">Loading security monitor…</p>;
  const s = data.stats || {};
  return (
    <div className="space-y-5" data-testid="security-monitor">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2.5">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-red-600 to-rose-700 flex items-center justify-center">
            <ShieldAlert className="w-5 h-5 text-white" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-slate-900">Security Monitoring</h2>
            <p className="text-xs text-slate-500">Login activity, lockouts, privileged actions & alerts</p>
          </div>
        </div>
        <div className="flex gap-1.5">
          {[7, 30, 90].map((d) => (
            <button key={d} onClick={() => { setDays(d); load(d); }}
              className={`px-2.5 py-1 rounded-lg text-xs font-medium ${days === d ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-600'}`}
              data-testid={`security-days-${d}`}>{d}d</button>
          ))}
          <button onClick={() => load()} className="px-2.5 py-1 rounded-lg bg-slate-100 text-slate-600" data-testid="security-refresh"><RefreshCw className="w-3.5 h-3.5" /></button>
        </div>
      </div>

      {/* alerts */}
      <div className="space-y-2" data-testid="security-alerts">
        {(data.alerts || []).map((a, i) => (
          <div key={i} className={`border rounded-xl p-3.5 flex items-start gap-2 ${SEV[a.severity] || SEV.info}`} data-testid={`security-alert-${a.type}`}>
            {a.severity === 'high' ? <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" /> : a.severity === 'medium' ? <ShieldAlert className="w-4 h-4 shrink-0 mt-0.5" /> : <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" />}
            <div><p className="text-sm font-semibold">{a.title}</p><p className="text-xs opacity-90">{a.detail}</p></div>
          </div>
        ))}
      </div>

      {/* stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="security-stats">
        <Stat icon={KeyRound} label="Failed logins" value={s.failed_attempts} tone={s.failed_attempts ? 'warn' : ''} />
        <Stat icon={Lock} label="Active lockouts" value={s.active_lockouts} tone={s.active_lockouts ? 'bad' : ''} />
        <Stat icon={KeyRound} label="Password changes" value={s.password_changes} />
        <Stat icon={UserCog} label="Admin actions" value={s.admin_actions} sub={`prev window: ${s.admin_actions_prev_window}`} />
        <Stat icon={UserCog} label="Permission changes" value={s.permission_changes} />
        <Stat icon={UserCog} label="Flag changes" value={s.feature_flag_changes} />
        <Stat icon={UserCog} label="New admin accounts" value={s.new_admin_accounts} tone={s.new_admin_accounts ? 'bad' : ''} />
        <Stat icon={KeyRound} label="Repeat offenders (≥5)" value={s.identifiers_over_threshold} tone={s.identifiers_over_threshold ? 'warn' : ''} />
      </div>

      {/* lockouts / repeated failures */}
      {(data.lockouts?.length > 0 || data.repeated_failures?.length > 0) && (
        <div className="bg-white border border-slate-200 rounded-xl p-4" data-testid="security-lockouts">
          <p className="text-xs font-bold text-slate-500 uppercase mb-2">Lockouts & repeated failures</p>
          <table className="w-full text-xs">
            <thead className="text-slate-400"><tr><th className="text-left py-1 font-medium">Identifier</th><th className="text-right font-medium">Attempts</th><th className="text-left pl-3 font-medium">Last attempt</th><th className="text-left pl-3 font-medium">Locked until</th></tr></thead>
            <tbody className="divide-y divide-slate-100">
              {[...(data.lockouts || []), ...(data.repeated_failures || []).filter((r) => !r.locked)].map((r, i) => (
                <tr key={i}>
                  <td className="py-1.5 text-slate-700 font-medium">{r.identifier}</td>
                  <td className="text-right text-slate-600">{r.attempts}</td>
                  <td className="pl-3 text-slate-500">{r.last_attempt_at}</td>
                  <td className={`pl-3 ${r.locked ? 'text-red-600 font-semibold' : 'text-slate-400'}`}>{r.locked ? r.locked_until : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* events */}
      <div className="bg-white border border-slate-200 rounded-xl p-4">
        <div className="flex items-center justify-between mb-2 gap-2">
          <p className="text-xs font-bold text-slate-500 uppercase">Security event log</p>
          <div className="flex items-center gap-1.5">
            <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && load(days, q)}
              placeholder="Search action / actor…" className="border border-slate-300 rounded-lg px-2.5 py-1 text-xs w-48" data-testid="security-search-input" />
            <button onClick={() => load(days, q)} className="p-1.5 rounded-lg bg-slate-100 text-slate-600" data-testid="security-search-btn"><Search className="w-3.5 h-3.5" /></button>
          </div>
        </div>
        <div className="max-h-80 overflow-y-auto divide-y divide-slate-100" data-testid="security-events">
          {events.map((e, i) => (
            <div key={i} className="py-1.5 flex items-center justify-between gap-2 text-xs">
              <span className="font-medium text-slate-700 truncate">{e.action}</span>
              <span className="text-slate-500 shrink-0">{e.actor} ({e.actor_role})</span>
              <span className="text-slate-400 shrink-0">{e.source_module}</span>
              <span className="text-slate-400 shrink-0">{e.timestamp}</span>
            </div>
          ))}
          {!events.length && <p className="text-xs text-slate-400 py-3">No security events match.</p>}
        </div>
      </div>
    </div>
  );
}

function Stat({ icon: Icon, label, value, sub, tone }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-3">
      <p className="text-[11px] text-slate-500 flex items-center gap-1"><Icon className="w-3.5 h-3.5" />{label}</p>
      <p className={`text-lg font-bold ${tone === 'bad' ? 'text-red-600' : tone === 'warn' ? 'text-amber-600' : 'text-slate-900'}`}>{value ?? 0}</p>
      {sub && <p className="text-[10px] text-slate-400">{sub}</p>}
    </div>
  );
}
